import { useState } from "react";
import { useNavigate, Link, useSearchParams } from "react-router-dom";
import { useAuth, formatApiError } from "../context/AuthContext";
import api from "../lib/api";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";

const titles = {
  login: "Welcome back",
  register: "Begin your story",
  forgot: "Reset your password",
  reset: "Choose a new password",
};
const subtitles = {
  login: "Sign in to continue your memoir.",
  register: "Create an account to start collecting your memories.",
  forgot: "Enter your email and we'll send you a reset link.",
  reset: "Enter a new password for your account.",
};

export default function AuthPage({ mode }) {
  const { login, register } = useAuth();
  const navigate = useNavigate();
  const [params] = useSearchParams();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [sent, setSent] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    try {
      if (mode === "login") {
        await login(email, password);
        navigate("/home");
      } else if (mode === "register") {
        await register(email, password);
        navigate("/start-trial");
      } else if (mode === "forgot") {
        await api.post("/auth/forgot-password", { email, origin_url: window.location.origin });
        setSent(true);
      } else if (mode === "reset") {
        await api.post("/auth/reset-password", { token: params.get("token"), password });
        toast.success("Password updated. Please sign in.");
        navigate("/login");
      }
    } catch (err) {
      toast.error(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="min-h-screen grain flex flex-col md:flex-row">
      <div className="hidden md:flex md:w-1/2 bg-teal text-cream flex-col justify-between p-12 relative z-10">
        <div className="font-serif text-2xl">Write My Story</div>
        <div>
          <h1 className="font-serif text-4xl lg:text-5xl leading-tight">
            Tell us your memories.<br />We'll help you write your memoir.
          </h1>
          <p className="mt-6 text-cream/80 text-lg max-w-md">
            You don't need to be a writer. Just answer one interesting question at a time — and
            watch your life story quietly take shape.
          </p>
        </div>
        <div className="text-cream/60 text-sm">A calm, private place for your story.</div>
      </div>

      <div className="flex-1 flex items-center justify-center p-6 sm:p-10 relative z-10">
        <div className="w-full max-w-sm animate-fade-up">
          <img src="/logo.png" alt="Write My Story" className="h-20 w-20 object-contain mb-4" />
          <h2 className="font-serif text-3xl text-charcoal">{titles[mode]}</h2>
          <p className="mt-2 text-subtle">{subtitles[mode]}</p>

          {mode === "forgot" && sent ? (
            <div className="mt-8 bg-sagelight rounded-2xl p-6 text-teal" data-testid="forgot-sent">
              If an account exists for that email, a reset link is on its way.
              <Link to="/login" className="block mt-4 text-teal font-semibold underline">Back to sign in</Link>
            </div>
          ) : (
            <form onSubmit={submit} className="mt-8 space-y-4">
              {mode !== "reset" && (
                <div>
                  <label className="text-sm font-medium text-charcoal">Email</label>
                  <input
                    data-testid="auth-email-input"
                    type="email" required value={email} onChange={(e) => setEmail(e.target.value)}
                    className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition"
                    placeholder="you@example.com"
                  />
                </div>
              )}
              {mode !== "forgot" && (
                <div>
                  <label className="text-sm font-medium text-charcoal">
                    {mode === "reset" ? "New password" : "Password"}
                  </label>
                  <input
                    data-testid="auth-password-input"
                    type="password" required value={password} onChange={(e) => setPassword(e.target.value)}
                    className="mt-1.5 w-full h-12 px-4 rounded-xl border border-line bg-white focus:border-teal focus:ring-2 focus:ring-teal/20 outline-none transition"
                    placeholder="••••••••"
                  />
                </div>
              )}
              <button
                data-testid="auth-submit-btn" type="submit" disabled={busy}
                className="w-full h-12 rounded-xl bg-teal hover:bg-tealdark text-cream font-semibold text-lg transition-colors flex items-center justify-center disabled:opacity-60"
              >
                {busy ? <Loader2 className="animate-spin" size={20} /> :
                  mode === "login" ? "Sign in" : mode === "register" ? "Create account" :
                  mode === "forgot" ? "Send reset link" : "Update password"}
              </button>
              {mode === "register" && (
                <p data-testid="auth-consent" className="text-xs text-subtle text-center leading-relaxed">
                  By creating an account you agree to our{" "}
                  <Link to="/terms" className="text-teal underline">Terms</Link> and{" "}
                  <Link to="/privacy" className="text-teal underline">Privacy Policy</Link>.
                </p>
              )}
            </form>
          )}

          {(mode === "login" || mode === "register") && (
            <div data-testid="google-auth-section" className="mt-5">
              <div className="flex items-center gap-3 text-xs text-subtle">
                <span className="flex-1 h-px bg-line" /> or <span className="flex-1 h-px bg-line" />
              </div>
              <button
                data-testid="google-signin-btn" type="button"
                onClick={() => {
                  // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
                  const redirectUrl = window.location.origin + "/home";
                  window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
                }}
                className="mt-4 w-full h-12 rounded-xl bg-white border border-line hover:border-sage text-charcoal font-semibold flex items-center justify-center gap-3 transition-colors"
              >
                <svg width="18" height="18" viewBox="0 0 48 48" aria-hidden="true"><path fill="#FFC107" d="M43.6 20.5H42V20H24v8h11.3c-1.6 4.7-6.1 8-11.3 8-6.6 0-12-5.4-12-12s5.4-12 12-12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 11.8 2 2 11.8 2 24s9.8 22 22 22 22-9.8 22-22c0-1.5-.2-2.6-.4-3.5z"/><path fill="#FF3D00" d="M6.3 14.7l6.6 4.8C14.7 15.1 19 12 24 12c3.1 0 5.9 1.2 8 3.1l5.7-5.7C34.6 4.1 29.6 2 24 2 15.6 2 8.3 6.8 6.3 14.7z"/><path fill="#4CAF50" d="M24 46c5.5 0 10.4-2.1 14.1-5.5l-6.5-5.5c-2 1.4-4.6 2.5-7.6 2.5-5.2 0-9.6-3.3-11.2-8l-6.5 5C8.2 41.1 15.5 46 24 46z"/><path fill="#1976D2" d="M43.6 20.5H42V20H24v8h11.3c-.8 2.2-2.2 4.1-4.1 5.5l6.5 5.5c-.5.4 7-5.1 7-15 0-1.5-.2-2.6-.4-3.5z"/></svg>
                Continue with Google
              </button>
            </div>
          )}

          <div className="mt-6 text-sm text-subtle space-y-2">
            {mode === "login" && (
              <>
                <div>New here? <Link data-testid="go-register" to="/register" className="text-teal font-semibold">Create an account</Link></div>
                <div><Link to="/forgot" className="text-teal">Forgot your password?</Link></div>
              </>
            )}
            {mode === "register" && (
              <div>Already have an account? <Link data-testid="go-login" to="/login" className="text-teal font-semibold">Sign in</Link></div>
            )}
            {(mode === "forgot" || mode === "reset") && (
              <Link to="/login" className="text-teal">Back to sign in</Link>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}
